import { useWeb3React } from "@web3-react/core";
import BigNumber from "bignumber.js";
import { useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import styled from "styled-components";
import Swal from "sweetalert2";
import { useVerifyCoupon } from "../../client/coupon/useCoupon";
import { useProducts } from "../../client/product/useProduct";
import { Address, useTransactionStore } from "../../client/product/useTransaction";
import Header from "../../components/Header";
import OrderSummary, { OrderProps } from "../../components/OrderSummary";
import Spinner from "../../components/Spinner";
import { Heading3 } from "../../components/Typography";
import { PAYMENT_METHOD_ETH, PAYMENT_METHOD_STRIPE, TOKENS } from "../../constants";
import { COLOR_WHITE } from "../../constants/colors";
import Countries from "../../data/countries.json";
import bgGray from "../../images/bg-gray.png";
import { CartItem } from "../../types/Product";
import CryptoPaymentMethod from "./CryptoPaymentMethod";
import CryptoPaymentModal from "./CryptoPaymentModal";
import PaymentForm from "./PaymentForm";
import StripePaymentMethod from "./StripePaymentMethod";
import Web3ConnectModal from "./Web3ConnectModal";

const Background = styled.div`
  min-height: 100vh;
  background-image: url(${bgGray});
  background-repeat: no-repeat;
  background-position: top;
  background-size: cover;
`;

const Title = styled.div`
  ${Heading3};
  color: ${COLOR_WHITE};
  margin-bottom: 44px;
  text-align: center;
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PaymentMehods = styled.div`
  max-width: 100%;
  width: 800px;

  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
`;

export default function Payment() {
  const transactionStore = useTransactionStore();

  const { activate, account } = useWeb3React();

  const storedProducts = localStorage.getItem("cart");
  const parsedStoredProducts: CartItem[] = JSON.parse(storedProducts || "[]");

  const storedPersonalInfo = localStorage.getItem("personalInfo");
  const parsedPersonalInfo: Address = JSON.parse(storedPersonalInfo || "{}");

  const [isWeb3ConnectModalOpen, setIsWeb3ConnectModalOpen] = useState(false);
  const [isCryptoPaymentModalOpen, setIsCryptoPaymentModalOpen] = useState(false);

  const [data, setData] = useState({
    firstName: parsedPersonalInfo.firstName || "",
    lastName: parsedPersonalInfo.lastName || "",
    address1: parsedPersonalInfo.address1 || "",
    address2: parsedPersonalInfo.address2 || "",
    pincode: parsedPersonalInfo.pincode || "",
    email: parsedPersonalInfo.email || "",
    phone: parsedPersonalInfo.phone || "",
    country: parsedPersonalInfo.country || "gb",
    region: parsedPersonalInfo.region || "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    pincode: "",
    email: "",
    phone: "",
    country: "",
    region: "",
  });

  const [productIds, setProductIds] = useState<string[]>([]);
  const { data: products, isLoading, isFetching } = useProducts(1, 1000, productIds.join(","));
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<OrderProps[]>([]);
  const [payment, setPayment] = useState(PAYMENT_METHOD_STRIPE);

  const isInformationValid = useMemo(() => {
    return (
      data.firstName &&
      data.lastName &&
      data.address1 &&
      data.pincode &&
      data.email &&
      data.country &&
      data.region &&
      !errors.firstName &&
      !errors.lastName &&
      !errors.address1 &&
      !errors.address2 &&
      !errors.pincode &&
      !errors.email &&
      !errors.phone &&
      !errors.country &&
      !errors.region
    );
  }, [errors, data]);

  const totalProductPrice = cartItems.reduce((acc, cur) => {
    const accTmp = new BigNumber(acc);
    const curPriceTmp = new BigNumber(cur?.price || 0);
    const curCountTmp = new BigNumber(cur.count);
    return accTmp.plus(curPriceTmp.times(curCountTmp)).toNumber();
  }, 0);
  const verifyCoupon = useVerifyCoupon();
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [totalShippingCost, setTotalShippingCost] = useState(0);
  const totalPrice = new BigNumber(totalProductPrice).minus(couponDiscount).plus(totalShippingCost).toNumber();
  const [token, setToken] = useState(TOKENS.ETH);
  const [couponUsed, setCouponUsed] = useState("");
  const [updatedPrice, setTotalPrice] = useState(totalPrice);
  const applyCoupon = async (promoCode: string) => {
    const res = await verifyCoupon.mutateAsync(promoCode);
    if (res && res.status) {
      setCouponUsed(promoCode);
      const discountAmount = (totalProductPrice + totalShippingCost) * (res.value / 100);
      setCouponDiscount(Number(discountAmount));
    }
  };

  useEffect(() => {
    const totalShipping = cartItems.reduce((acc: any, product: any) => {
      const accTemp = new BigNumber(acc);
      return accTemp.plus(product.shipping ||0 ).toNumber();
    }, 0);

    setTotalShippingCost(totalShipping);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems]);

  useEffect(() => {
    if (payment === PAYMENT_METHOD_ETH && !account) {
      setIsWeb3ConnectModalOpen(true);
    }
  }, [account, activate, payment]);

  useEffect(() => {
    if (account) {
      setPayment(PAYMENT_METHOD_ETH);
    } else {
      setPayment(PAYMENT_METHOD_STRIPE);
    }
  }, [account, isWeb3ConnectModalOpen]);

  useEffect(() => {
    const productIdsTemp = parsedStoredProducts.map((item) => item.id);
    setProductIds(productIdsTemp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!(products && products.data.length > 0)) return;
    const cartItemsTemp: CartItem[] = [];
    parsedStoredProducts.forEach((cartItem) => {
      const product = products.data.find((item) => item._id === cartItem.id);

      cartItemsTemp.push({
        id: cartItem.id,
        name: product ? product.name : "",
        image: product ? product.images[0] : "",
        price: product ? product.price : 0,
        count: cartItem ? cartItem.count : 0,
        option: cartItem ? cartItem.option : "",
        storedAt: cartItem ? cartItem.storedAt : 0,
        shipping: cartItem ? cartItem.shipping : 0,
      });
    });
    setCartItems(cartItemsTemp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products?.data]);

  useEffect(() => {
    const newOrders: OrderProps[] = cartItems.map((cartItem) => {
      const { name, price, count } = cartItem;
      const itemTotalPrice = new BigNumber(price || 0).times(count);
      return {
        label: name || "",
        qty: cartItem.count,
        price: cartItem.price,
        totalPrice: itemTotalPrice.toNumber(),
      };
    });
    setOrders([
      ...newOrders,
      {
        label: "Shipping Fee",
        totalPrice: totalShippingCost,
      },
    ]);
  }, [cartItems, totalShippingCost]);

  const onSetData = (newUpdate: { [key: string]: string }) => {
    setData({
      ...data,
      ...newUpdate,
    });
  };

  const onError = (newErrors: { [key: string]: string }) => {
    setErrors({
      ...errors,
      ...newErrors,
    });
  };

  const handleStripePayment = async () => {
    try {
      const countryName = Countries.find((country) => country.countryCode === data.country)!.name;
      const result = await transactionStore.mutateAsync({
        products: cartItems.map((item) => ({
          _id: item.id,
          count: item.count,
          sku: {
            option: item.option,
          },
        })),
        address: { ...data, country: countryName },
        paymentMethod: PAYMENT_METHOD_STRIPE,
        coupon: couponUsed,
      });

      if (result.status === 201) {
        window.location.href = result.data;
      }
    } catch (error: any) {
      const errMessage = error.response?.data?.message || error.message;

      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: errMessage,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      // eslint-disable-next-line no-console
      console.warn(error);
    }
  };

  const handleEthPayment = () => {
    setIsCryptoPaymentModalOpen(true);
  };

  const handlePlaceOrder = async () => {
    localStorage.setItem("personalInfo", JSON.stringify(data));
    if (payment === PAYMENT_METHOD_STRIPE) {
      await handleStripePayment();
    } else {
      handleEthPayment();
    }
  };

  return (
    <>
      <Background>
        <Header />
        <StyledContainer>
          {(isLoading || isFetching) && <Spinner animation="border" />}
          {!isLoading && !isFetching && (
            <>
              <Title>Fill in your information</Title>
              <PaymentForm data={data} onChange={onSetData} errors={errors} onError={onError} />

              <Title>Select your payment method</Title>
              <PaymentMehods>
                <StripePaymentMethod setPaymentMethod={setPayment} selectedPayment={payment} />
                <CryptoPaymentMethod
                  setPaymentMethod={setPayment}
                  selectedPayment={payment}
                  setIsWeb3ConnectModalOpen={setIsWeb3ConnectModalOpen}
                  setToken={setToken}
                  token={token}
                />
              </PaymentMehods>
              <OrderSummary
                size="md"
                title="Order Summary"
                orders={orders}
                handlePlaceOrder={handlePlaceOrder}
                totalPrice={totalPrice}
                isLoading={transactionStore.isLoading}
                isDisabled={!isInformationValid}
                applyCoupon={applyCoupon}
                error={
                  isInformationValid
                    ? ""
                    : "Please ensure data is entered correctly and all required fields are populated"
                }
              />
            </>
          )}
        </StyledContainer>
      </Background>
      <CryptoPaymentModal
        isOpen={isCryptoPaymentModalOpen}
        handleClose={() => setIsCryptoPaymentModalOpen(false)}
        data={data}
        cartItems={cartItems}
        token={token}
      />
      <Web3ConnectModal isOpen={isWeb3ConnectModalOpen} handleClose={() => setIsWeb3ConnectModalOpen(false)} />
    </>
  );
}
